export enum SerpQueryProfile {
  EbaySearch,
  SellerSold,
  EbayProduct,
}

export interface ISerpQueryParameter {
  name: string;
  value: string;
  sortOrder: number;
}

export interface ISerpQueryProfileSettings {
  profile: SerpQueryProfile;
  usingDefaults: boolean;
  parameters: ISerpQueryParameter[];
  /** The saved query as it runs, with {{tag}} for the per-call values and the API key. */
  queryTemplate?: string;
}

/**
 * SerpApi's playground reads the query string, so a query can be run by hand there. The
 * API key is left off: the playground signs the request with the logged-in account's key.
 */
export function serpPlaygroundUrl(query: string) {
  const withoutKey = query
    .split("&")
    .filter((part) => !part.toLowerCase().startsWith("api_key="))
    .join("&");

  return `https://serpapi.com/playground?${withoutKey}`;
}

/**
 * Rewrites the per-search parts of an active-listings query so the preview follows the unsaved
 * form. A null value drops the parameter, matching the real search. Fixed parts are untouched.
 */
export function withSearchValues(
  query: string,
  values: { _nkw: string | null; category_id: string | null },
) {
  const parts = query.split("&").filter(Boolean);
  const apiKeyIndex = parts.findIndex((part) => part.toLowerCase().startsWith("api_key="));
  const fixed = parts.filter((part) => {
    const name = part.split("=")[0];
    return !(name in values) && !part.toLowerCase().startsWith("api_key=");
  });

  const dynamic = Object.entries(values)
    .filter(([, value]) => value && value.trim().length > 0)
    .map(([name, value]) => `${name}=${encodeURIComponent((value as string).trim())}`);

  return [...fixed, ...dynamic, ...(apiKeyIndex >= 0 ? [parts[apiKeyIndex]] : [])].join("&");
}

export interface ISerpQuerySettings {
  profiles: ISerpQueryProfileSettings[];
}

export interface SerpQueryField {
  label: string;
  hint: string;
  /** One eBay username per line. Stored as a comma-separated query value. */
  sellers?: boolean;
}

export const serpQueryFields: Record<string, SerpQueryField> = {
  engine: { label: "Engine", hint: "SerpApi engine name." },
  ebay_domain: { label: "eBay site", hint: "For example ebay.co.uk." },
  _salic: { label: "Seller location", hint: "3 is the United Kingdom." },
  LH_ItemCondition: { label: "Condition", hint: "3000 is Used." },
  _saslop: { label: "Seller match", hint: "1 includes only the sellers below." },
  _sasl: {
    label: "Sellers",
    hint: "One eBay username per line. These are the breaker accounts the live search includes.",
    sellers: true,
  },
  show_only: { label: "Show only", hint: "Sold limits the search to completed listings." },
  category_id: { label: "eBay category", hint: "6030 is eBay Motors." },
  locale: { label: "Locale", hint: "GB for the United Kingdom." },
};

export const serpQueryProfiles: {
  profile: SerpQueryProfile;
  title: string;
  description: string;
  reserved: string;
}[] = [
  {
    profile: SerpQueryProfile.EbaySearch,
    title: "Active listings",
    description:
      "Used when a lead or quote is priced. Each search adds its own keyword and eBay category.",
    reserved: "Keyword, eBay category and the API key are added per search.",
  },
  {
    profile: SerpQueryProfile.SellerSold,
    title: "Seller sold listings",
    description:
      "Used by Seller History. Each scrape adds the seller, the page and the page size.",
    reserved: "Seller, page, page size and the API key are added per scrape.",
  },
  {
    profile: SerpQueryProfile.EbayProduct,
    title: "Product detail",
    description: "Used when a sold listing is opened. Each lookup adds the product id.",
    reserved: "Product id and the API key are added per lookup.",
  },
];

export function sellersToLines(value: string) {
  return value
    .split(/[,\n\r]+/)
    .map((seller) => seller.trim())
    .filter(Boolean)
    .join("\n");
}

export function fieldFor(name: string) {
  return serpQueryFields[name] ?? serpQueryFields[name.toLowerCase()];
}
